import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	ActionRendererKeyToPayload,
	ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';

import { formatDateForDisplay } from './formatDateForDisplay';

export type Payload = ActionRendererKeyToPayload[typeof ActionKeys.CreateProjectProgressUpdate];

const styles = cssMap({
	container: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		paddingTop: token('space.050'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		color: token('color.text.subtle'),
	},
	icon: {
		display: 'inline-flex',
		color: token('color.text.subtle'),
	},
});

export const TargetDateField = ({
	targetDate,
	fallbackDate,
}: {
	targetDate?: ActionWith<Payload>['data']['targetDate'];
	fallbackDate?: string | null;
}): React.JSX.Element | null => {
	const intl = useIntl();

	if (!targetDate?.date && !fallbackDate) {
		return null;
	}

	return (
		<Inline testId="project-status-update-action-renderer.ui.target-date">
			<Inline xcss={styles.container}>
				<Inline xcss={styles.icon}>
					<CalendarIcon size="medium" label="" />
				</Inline>
				<Text size="small">
					{targetDate?.date
						? formatDateForDisplay(targetDate.date, intl.locale, targetDate.confidence)
						: fallbackDate}
				</Text>
			</Inline>
		</Inline>
	);
};
